import { FC, useContext, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../context/notificationContext";
import { useI18n } from "../../../hooks/useTranslations";
import { useAdminCostsApi } from "../../../http/costs/api";
import { getMissingCurrency, isMissingCurrencyError } from "../../../http/errors";
import Button from "../../ui/Button";
import ModalDialog from "../../ui/ModalDialog";

interface ImportDkvCostImportButtonProps {
  isDisabled?: boolean;
  onUpdate: () => void;
}

const ImportDkvCostImportButton: FC<ImportDkvCostImportButtonProps> = ({
  isDisabled,
  onUpdate,
}: ImportDkvCostImportButtonProps) => {
  const i18n = useI18n();
  const costApi = useAdminCostsApi();
  const dispatch = useContext(NotificationDispatch);
  const { id } = useParams<{ id: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const importCosts = async () => {
    setIsSubmitting(true);
    try {
      await costApi.importCosts(id!, "dkv");
      dispatch(showSuccessNotification(i18n.t));
      onUpdate();
      setOpenDialog(false);
    } catch (error) {
      if (isMissingCurrencyError(error)) {
        const missingCurrency = getMissingCurrency(error);
        const msg = i18n.t.costs.errors.missingCurrency(missingCurrency ?? "");
        dispatch(showErrorNotification(i18n, error, msg));
      } else {
        dispatch(showErrorNotification(i18n, error));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        buttonProps={{
          onClick: () => setOpenDialog(true),
          disabled: !!isDisabled || isSubmitting,
        }}
      >
        {i18n.t.common.importInCosts}
      </Button>
      {!!openDialog && (
        <ModalDialog
          onClose={() => setOpenDialog(false)}
          title={i18n.t.common.importInCosts}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.t.common.cancel,
              type: "secondary",
              onClick: () => setOpenDialog(false),
            },
            {
              title: i18n.t.common.yes,
              onClick: importCosts,
            },
          ]}
        >
          {i18n.t.costsImport.questions.importInCosts}
        </ModalDialog>
      )}
    </>
  );
};

export default ImportDkvCostImportButton;
